// ── Visual workflow validation ────────────────────────────────────────────────
// Pure helpers (no React) so they can be unit-tested and reused. They surface a
// human-readable message for every structural problem in the builder — chiefly
// "this block is not joined into the flow" — which the builder shows inline and
// blocks Save on.

import {
  BRANCH_HANDLE_TRUE,
  BRANCH_HANDLE_FALSE,
  SWITCH_HANDLE_DEFAULT,
  LOOP_HANDLE_BODY,
  LOOP_HANDLE_AFTER,
} from './types';

export type WorkflowIssueSeverity = 'error' | 'warning';

export interface WorkflowIssue {
  /** Offending node id (omitted for whole-graph issues like "no trigger"). */
  nodeId?: string;
  nodeLabel?: string;
  severity: WorkflowIssueSeverity;
  message: string;
}

/** Minimal shapes so this stays decoupled from @xyflow/react generics. */
interface ValNode {
  id: string;
  type?: string;
  data?: { kind?: string; label?: string } & Record<string, unknown>;
}
interface ValEdge {
  source: string;
  target: string;
  sourceHandle?: string | null;
}

// A workflow STARTS at a trigger (no incoming required) and ENDS at end/stop
// (no outgoing required). Everything else must be wired on both sides.
const SOURCE_KINDS = new Set(['trigger']);
const TERMINAL_KINDS = new Set(['end', 'stop']);

const PRETTY_KIND: Record<string, string> = {
  trigger: 'Trigger',
  classify: 'AI Classify',
  branch: 'If / Branch',
  switch: 'Switch',
  merge: 'Merge',
  action: 'Action',
  delay: 'Delay',
  loop: 'Loop',
  end: 'End',
  stop: 'Stop',
};

function kindOf(node: ValNode): string {
  return (node.data?.kind as string) || node.type || 'unknown';
}

function nameOf(node: ValNode): string {
  const label = (node.data?.label as string)?.trim();
  return label || PRETTY_KIND[kindOf(node)] || 'Block';
}

/**
 * Validate the visual workflow graph. Returns one issue per problem found:
 *   - error: blocks Save (disconnected blocks, no trigger, empty graph).
 *   - warning: shown but does NOT block Save (e.g. a branch missing a path).
 * Returns [] when the workflow is structurally sound.
 */
export function validateWorkflow(nodes: ValNode[], edges: ValEdge[]): WorkflowIssue[] {
  const issues: WorkflowIssue[] = [];

  if (!nodes || nodes.length === 0) {
    return [{ severity: 'error', message: 'The workflow is empty — add a Trigger block to begin.' }];
  }

  const incoming = new Map<string, number>();
  const outgoing = new Map<string, number>();
  for (const e of edges || []) {
    outgoing.set(e.source, (outgoing.get(e.source) || 0) + 1);
    incoming.set(e.target, (incoming.get(e.target) || 0) + 1);
  }

  const triggers = nodes.filter((n) => kindOf(n) === 'trigger');
  if (triggers.length === 0) {
    issues.push({ severity: 'error', message: 'No Trigger block — every workflow must start with a Trigger.' });
  } else if (triggers.length > 1) {
    // Several triggers are supported: the workflow starts on any of them, each
    // running its own path. Two IDENTICAL triggers, though, both start on the
    // same event — usually a copy-paste mistake.
    const seen = new Map<string, ValNode>();
    for (const t of triggers) {
      const d = (t.data ?? {}) as Record<string, unknown>;
      const key = [d.eventType ?? '', d.channel ?? 'any', d.ticketTypeId ?? ''].join('|');
      const first = seen.get(key);
      if (first) {
        issues.push({
          nodeId: t.id,
          nodeLabel: nameOf(t),
          severity: 'warning',
          message: `"${nameOf(t)}" is set up exactly like "${nameOf(first)}" — both start on the same event, so both paths run.`,
        });
      } else {
        seen.set(key, t);
      }
    }
  }

  for (const node of nodes) {
    const kind = kindOf(node);
    const label = nameOf(node);
    const inCount = incoming.get(node.id) || 0;
    const outCount = outgoing.get(node.id) || 0;
    const needsIncoming = !SOURCE_KINDS.has(kind);
    const needsOutgoing = !TERMINAL_KINDS.has(kind);

    // Fully isolated block — the clearest "not joined" case.
    if (needsIncoming && needsOutgoing && inCount === 0 && outCount === 0) {
      issues.push({
        nodeId: node.id,
        nodeLabel: label,
        severity: 'error',
        message: `"${label}" is not connected to anything — link it into the flow or remove it.`,
      });
      continue;
    }
    if (needsIncoming && inCount === 0) {
      issues.push({
        nodeId: node.id,
        nodeLabel: label,
        severity: 'error',
        message: `"${label}" has no incoming connection — nothing leads into this block.`,
      });
    }
    if (needsOutgoing && outCount === 0) {
      issues.push({
        nodeId: node.id,
        nodeLabel: label,
        severity: 'error',
        message: `"${label}" has no outgoing connection — connect it to the next block (or an End/Stop).`,
      });
    }
  }

  // A branch with a missing Yes/No path is usually a mistake, but not fatal.
  for (const node of nodes) {
    if (kindOf(node) !== 'branch') continue;
    const handles = new Set(
      (edges || []).filter((e) => e.source === node.id).map((e) => e.sourceHandle ?? ''),
    );
    const missing: string[] = [];
    if (!handles.has(BRANCH_HANDLE_TRUE)) missing.push('"Yes"');
    if (!handles.has(BRANCH_HANDLE_FALSE)) missing.push('"No"');
    if (missing.length > 0 && (outgoing.get(node.id) || 0) > 0) {
      issues.push({
        nodeId: node.id,
        nodeLabel: nameOf(node),
        severity: 'warning',
        message: `"${nameOf(node)}" is missing a ${missing.join(' and ')} path.`,
      });
    }
  }

  // A switch that declares a Default case but never connects it — warning only.
  for (const node of nodes) {
    if (kindOf(node) !== 'switch') continue;
    if (node.data?.hasDefault !== true) continue;
    const handles = new Set(
      (edges || []).filter((e) => e.source === node.id).map((e) => e.sourceHandle ?? ''),
    );
    if (!handles.has(SWITCH_HANDLE_DEFAULT) && (outgoing.get(node.id) || 0) > 0) {
      issues.push({
        nodeId: node.id,
        nodeLabel: nameOf(node),
        severity: 'warning',
        message: `"${nameOf(node)}" has a Default case with no path connected.`,
      });
    }
  }

  // A loop should have both a body ("Each item") and an after ("After") path.
  for (const node of nodes) {
    if (kindOf(node) !== 'loop') continue;
    if ((outgoing.get(node.id) || 0) === 0) continue; // already flagged as disconnected above
    const handles = new Set(
      (edges || []).filter((e) => e.source === node.id).map((e) => e.sourceHandle ?? ''),
    );
    const missing: string[] = [];
    if (!handles.has(LOOP_HANDLE_BODY)) missing.push('"Each item"');
    if (!handles.has(LOOP_HANDLE_AFTER)) missing.push('"After"');
    if (missing.length > 0) {
      issues.push({
        nodeId: node.id,
        nodeLabel: nameOf(node),
        severity: 'warning',
        message: `"${nameOf(node)}" is missing a ${missing.join(' and ')} path.`,
      });
    }
  }

  // "Move to pipeline stage" actions that are wired in but have no target stage
  // selected yet (they would silently no-op at runtime).
  for (const node of nodes) {
    if (kindOf(node) !== 'action') continue;
    const actionType = (node.data as { actionType?: string } | undefined)?.actionType;
    const config = ((node.data as { config?: Record<string, unknown> } | undefined)?.config) ?? {};
    if (
      actionType === 'auto_assign' &&
      !['round_robin', 'least_busy', 'random'].includes(config.strategy as string)
    ) {
      // Blocking: without a technique the step can never assign anyone.
      issues.push({
        nodeId: node.id,
        nodeLabel: nameOf(node),
        severity: 'error',
        message: `"${nameOf(node)}" (Auto-assign agent) needs an assignment technique — Round Robin, Least Busy or Random.`,
      });
    } else if (actionType === 'move_to_pipeline_stage' && !config.stageId) {
      issues.push({
        nodeId: node.id,
        nodeLabel: nameOf(node),
        severity: 'warning',
        message: `"${nameOf(node)}" (Move to pipeline stage) needs a target stage selected.`,
      });
    } else if (actionType === 'create_ticket') {
      const missing: string[] = [];
      if (!config.subject) missing.push('a subject');
      if (!config.ticketTypeId) missing.push('a ticket type');
      if (missing.length > 0) {
        issues.push({
          nodeId: node.id,
          nodeLabel: nameOf(node),
          severity: 'warning',
          message: `"${nameOf(node)}" (Create linked ticket) needs ${missing.join(' and ')}.`,
        });
      }
    }
  }

  return issues;
}

/** Convenience: just the node ids that have a blocking (error) issue. */
export function invalidNodeIds(issues: WorkflowIssue[]): Set<string> {
  return new Set(issues.filter((i) => i.severity === 'error' && i.nodeId).map((i) => i.nodeId as string));
}
